import { useContext, useEffect, useState } from 'react';
import { optionalProp } from '../core';
import { useConfig } from '../Config/ConfigProvider';
import { ViewPortSizes } from '../Breakpoint/BreakpointProvider';
import FallbackNextgenImage from './FallbackNextgenImage';
import { NextgenImageContext, NextgenImageProvider } from './NextgenImageProvider';
import { type ImageSize, LoaderType, type NextgenImageProps } from './types/propsTypes';

const replaceCSToCDN = (
  url: string,
  useCDNForCS: boolean,
  csImageUrl: string,
  CDN_URL: string,
  csCdnImageUrl?: string,
  forceCdnUrlConversion?: boolean,
) => {
  const urlToCheck = forceCdnUrlConversion ? (csCdnImageUrl as string) : csImageUrl;
  return useCDNForCS && url?.includes(urlToCheck) ? url.replace(urlToCheck, CDN_URL) : url;
};

const getContentStackURL = (
  src: string,
  useCDNForCS: boolean,
  CDN_URL: string,
  csImageUrl: string,
  width: number,
  format: string,
  quality: NextgenImageProps['quality'],
  fallbackQuality: number,
  isRelativeURL: boolean,
  csCdnImageUrl?: string,
  forceCdnUrlConversion?: boolean,
) => {
  const queryString = `quality=${quality || fallbackQuality}${width ? `&width=${width}` : ``}&format=${
    format ?? 'avif'
  }`;
  if (isRelativeURL) {
    return `${
      useCDNForCS ? CDN_URL : forceCdnUrlConversion ? (csCdnImageUrl as string) : csImageUrl
    }${src}?${queryString}`;
  } else {
    const delimiter = src.includes('?') ? '&' : '?';
    return `${src}${delimiter}${queryString}`;
  }
};

export const getQuality = (loaderType: NextgenImageProps['loaderType'], quality: NextgenImageProps['quality']) =>
  loaderType === LoaderType.CONTENTSTACK || !quality ? {} : { quality };

const getObserverActions = (
  imageElement: Element | null,
  restProps: Partial<NextgenImageProps>,
  hasBeenObserved: boolean,
) => !imageElement || restProps.priority || hasBeenObserved;

export const noFilledImage = (height: number | `${number}` | undefined, width: number | `${number}` | undefined) =>
  height && width;

export const getNextImageSizes = (
  height: number | `${number}` | undefined,
  width: number | `${number}` | undefined,
  imageWidths: ImageSize | undefined,
  fill: boolean,
): string | undefined => {
  if (!noFilledImage(height, width) && imageWidths && fill) {
    const breakpointToMedia = {
      small: `(max-width: ${ViewPortSizes.MEDIUM}px)`,
      medium: `(max-width: ${ViewPortSizes.LARGE}px)`,
      large: `(max-width: ${ViewPortSizes.XLARGE}px)`,
    };

    const order = ['small', 'medium', 'large', 'xlarge'] as const;

    const sizeParts = order
      .filter((key) => imageWidths[key] !== undefined)
      .map((key) => {
        if (key === 'xlarge') {
          return `${imageWidths[key]}px`;
        }
        return `${breakpointToMedia[key]} ${imageWidths[key]}px`;
      });
    return sizeParts.join(', ');
  } else {
    return undefined;
  }
};

const NextgenImage = ({
  fill = true,
  quality = 100,
  // TODO: Make CONTENTSTACK value default after PODs make their changes
  loaderType,
  isRelativeURL = false,
  fallbackQuality = 100,
  format = 'avif',
  src,
  forceCdnUrlConversion = false,
  imageWidths,
  isSensitiveField = false,
  ...restProps
}: NextgenImageProps) => {
  const { element: NextJsImage, useCDNForCS } = useContext(NextgenImageContext);
  const configs = useConfig();
  const [isAboveFold, setIsAboveFold] = useState(false);
  const [hasBeenObserved, setHasBeenObserved] = useState(false); // Track if image has been observed

  const csImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_IMAGES_URL as string;
  const csCdnImageUrl = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_URL as string;
  const CDNPostFix = configs.NEXT_PUBLIC_CONTENT_STACK_CDN_POSTFIX as string;
  const CDN_URL = `${configs.DEFAULT_HOST_URL}${CDNPostFix}`;
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;

  const updatedSRC = replaceCSToCDN(
    src as string,
    useCDNForCS,
    csImageUrl,
    CDN_URL,
    csCdnImageUrl,
    forceCdnUrlConversion,
  );
  useEffect(() => {
    const imageElement = document.querySelector(`img[src="${updatedSRC}"]`);
    if (getObserverActions(imageElement, restProps, hasBeenObserved)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAboveFold(entry?.isIntersecting as boolean);
        setHasBeenObserved(true);
      },
      {
        rootMargin: '0px',
        threshold: 0.2, // Trigger when 20% of the image is in view
      },
    );

    observer.observe(imageElement as Element);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [updatedSRC, restProps.priority, hasBeenObserved]);

  const customLoader = ({ src, width }: { src: string; width: number }) => {
    if (loaderType === LoaderType.CONTENTSTACK) {
      return getContentStackURL(
        src,
        useCDNForCS,
        CDN_URL,
        csImageUrl,
        width,
        format,
        quality,
        fallbackQuality,
        isRelativeURL,
        csCdnImageUrl,
        forceCdnUrlConversion,
      );
    } else if (loaderType === LoaderType.WEBFONTS && isRelativeURL) {
      return `${configs.NEXT_ASSETS_BASE_PATH}${src}`;
    }

    return updatedSRC;
  };

  const renderNextImage = NextJsImage && !!updatedSRC;
  const noFilledImage = restProps.height && restProps.width;

  return renderNextImage ? (
    <NextJsImage
      src={updatedSRC}
      loader={customLoader}
      {...getQuality(loaderType, quality)}
      fill={noFilledImage ? false : fill}
      priority={isAboveFold || restProps.priority}
      {...restProps}
      sizes={restProps.sizes ?? getNextImageSizes(restProps.height, restProps.width, imageWidths, fill)}
      data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
    />
  ) : (
    <FallbackNextgenImage
      src={updatedSRC}
      {...optionalProp('isSensitiveField', allowMasking ? isSensitiveField : undefined)}
      {...restProps}
    />
  );
};

export { replaceCSToCDN, getContentStackURL, NextgenImage, NextgenImageProvider, LoaderType };
export type { NextgenImageProps };
